import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useEffect, useMemo } from "react";
import { useGetPolicyQuery, PolicyContractType, useGetPolicyTATQuery, } from "../../api/ebam/policy/policy-api";
import { selectPolicyType, selectProposalIdForPolicy, } from "../../features/ebam/plicy-data-page/policy-data-page.slice";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { usePopulatePolicy } from "./use-populate-policy";
import { TATItems } from "../../features/ebam/plicy-data-page/policy-tat/policy-tat.interface";
import { usePolicyDataState } from "../policy/use-policy-data-state";
import { usePolicySocket } from "../socket/use-policy-socket";

export const usePolicyData = () => {
    
    const { 
        contractType, 
        typeOfPolicy: policyType,
        policyId: proposalId
    } = usePolicyDataState()
    
    usePolicySocket()

    const policyQueryArg =
        proposalId != null && contractType
            ? { policyId: proposalId, type: contractType }
            : skipToken;
    const {
        data: policy,
        error: errorPolicy,
        isError: isErrorPolicy,
        isLoading: isLoadingPolicy,
        refetch: refetchPolicy,
        isFetching: isFetchingPolicy,
        fulfilledTimeStamp: fulfilledTimeStampPolicy,
    } = useGetPolicyQuery(policyQueryArg);
    const errorMessagePolicy = getErrorMessage(errorPolicy);

    const {
        data: policyTAT,
        error: errorPolicyTAT,
        isError: isErrorPolicyTAT,
        isLoading: isLoadingPolicyTAT,
        refetch: refetchPolicyTAT,
        isFetching: isFetchingPolicyTAT,
    } = useGetPolicyTATQuery(proposalId !== null ? { policyId: proposalId } : skipToken );
    const errorMessagePolicyTAT = getErrorMessage(errorPolicyTAT);

    const tatItemsApi: TATItems[] = useMemo(() => {
        return (
            policyTAT?.items?.map((item) => ({
                tat_id: item.tat_id,
                sort_order: item.sort_order,
                category: item.category,
                activity: item.activity,
                tat: item.tat,
                reckoning_date: item.reckoning_date,
                mode_of_communication:
                    item.mode_of_communication,
                responsible: item.responsible,
                contact_details: item.contact_details,
            })) ?? []
        );
    }, [policyTAT]);

    const {
        populatePolicyInformation,
        populateScheduleOfInsurance,
        populateSpecialUnderwritingProvisions,
        populateSigningAddressAndDocumentCode,
    } = usePopulatePolicy({
        policy,
        policyType,
    });

    const repopulateAll = useCallback(() => {
        populatePolicyInformation()
        populateScheduleOfInsurance()
        populateSpecialUnderwritingProvisions()
        populateSigningAddressAndDocumentCode()
    }, [
        populatePolicyInformation,
        populateScheduleOfInsurance,
        populateSpecialUnderwritingProvisions,
        populateSigningAddressAndDocumentCode,
    ])

    useEffect(() => {
        if (!policy?.application_id) return;
        
        repopulateAll()
    }, [
        policy,
        repopulateAll
    ]);

    return {
        policy,

        isLoadingPolicy,
        isFetchingPolicy,
        isErrorPolicy,
        refetchPolicy,
        errorMessagePolicy,

        tatItemsApi,
        policyTAT,
        isLoadingPolicyTAT,
        isFetchingPolicyTAT,
        isErrorPolicyTAT,
        refetchPolicyTAT,
        errorMessagePolicyTAT,

        populatePolicyInformation,
        populateScheduleOfInsurance,
        populateSpecialUnderwritingProvisions,
        populateSigningAddressAndDocumentCode,

        repopulateAll,
    };
};